import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useForgotPasswordMutation } from '../../../api/registration/forgotPassword';
import ControlledTextField from '../../../components/TextField/ControlledTextField';
import Button from '../../../components/styledComponents/Button';
import { RemoveButton } from '../../../components/styledComponents/IconButtons';
import colors from '../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import {
  ForgotPasswordFormValues,
  forgotPasswordValidationSchema,
} from '../DirectAccess/schemas/forgotPassword';

interface ForgotPasswordDialogProps {
  open: boolean;
  closeDialog: () => void;
}

const ForgotPasswordDialog = ({
  closeDialog,
  open,
}: ForgotPasswordDialogProps) => {
  const [showForm, setShowForm] = useState(true);
  const forgotPasswordMutation = useForgotPasswordMutation();
  const { enqueueSnackbar } = useSnackbarsContext();
  const { track } = useDirectAccessAnalytics();

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount },
    resetField,
    reset,
    watch,
  } = useForm<ForgotPasswordFormValues>({
    resolver: yupResolver(forgotPasswordValidationSchema),
    mode: 'onChange',
  });
  const hasSubmitted = submitCount > 0;

  const email = watch('email');

  const sendForgotPasswordEmail = async (values: ForgotPasswordFormValues) => {
    track('Reset Password Initiated', {
      event_type: 'User Information Entered',
      location: 'Feature Card',
    });
    try {
      await forgotPasswordMutation.mutateAsync(values.email.toLowerCase());
      return setShowForm(false);
    } catch (err) {
      enqueueSnackbar({
        variant: 'error',
        key: 'email-forgot-password-error',
        content: 'Something went wrong. Please try again.',
      });
      handleClose();
    }
  };

  const handleClose = () => {
    closeDialog();
    reset();
    setShowForm(true);
  };

  return (
    <Dialog
      aria-labelledby="forgotPasswordLabel"
      data-testid="forgot-password-dialog"
      open={open}
      onClose={handleClose}
      sx={{ backdropFilter: 'blur(5px)' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            margin: 0,
            px: { mobile: 3, tablet: 4 },
            py: { mobile: 3, tablet: 3 },
            position: { mobile: 'fixed', tablet: 'relative' },
            width: { mobile: 1, tablet: 480 },
            height: 'auto',
            maxHeight: 1,
          },
          id: 'forgot-password-dialog',
        }
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
          width: 1
        }}>
        <RemoveButton
          aria-label="Close reset password dialog"
          data-testid="close-forgot-password-dialog-button"
          onClick={handleClose}
          autoFocus
        />
      </Stack>
      <DialogTitle
        id="forgotPasswordLabel"
        sx={{
          fontSize: 28,
          fontWeight: 700,
          textAlign: 'center',
          p: 0,
          my: { mobile: 2, tablet: 3 },
        }}
      >
        Reset your password
      </DialogTitle>
      <DialogContent
        sx={{
          px: 0,
          pb: 3,
        }}
      >
        <Stack
          component="form"
          direction="column"
          onSubmit={handleSubmit(sendForgotPasswordEmail)}
        >
          {showForm && hasSubmitted && (
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: { mobile: 16, tablet: 18 },
                lineHeight: '24px',
                mb: 1,
              }}
            >
              If you didn&apos;t receive the email, check that you&apos;re
              entering the email address that matches your account and try
              again.
            </Typography>
          )}
          {showForm && (
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: { mobile: 16, tablet: 18 },
                lineHeight: '24px',
              }}
            >
              Enter the email address you use for MyPolicy. We&apos;ll email
              instructions to reset your password.
            </Typography>
          )}
          {!showForm && (
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: { mobile: 16, tablet: 18 },
                lineHeight: '24px',
              }}
            >
              We&apos;ve sent instructions to {email} to reset your password. It
              may take several minutes to arrive.
            </Typography>
          )}
          {!showForm && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                mt: 1
              }}>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontWeight: 400,
                  fontSize: { mobile: 16, tablet: 18 },
                  lineHeight: '24px',
                }}
              >
                Didn&apos;t receive the email?{' '}
              </Typography>
              <Button
                fullWidth={false}
                type="button"
                onClick={() => {
                  resetField('email');
                  setShowForm(true);
                }}
                disableRipple
                disableTouchRipple
                disableFocusRipple
                data-testid="resend-email-button"
                variant="text"
                sx={{
                  px: 0,
                  py: 0,
                  fontSize: 'inherit',
                  width: 'auto',
                }}
              >
                Resend email
              </Button>
            </Stack>
          )}
          {showForm && (
            <ControlledTextField
              name="email"
              control={control}
              className="csaa-qm-encrypt"
              label="Email"
              sx={{ mt: { mobile: 3, tablet: 2 } }}
              inputProps={{
                autoComplete: 'email',
              }}
              InputProps={{ required: true }}
            />
          )}
          <Button
            fullWidth
            data-testid="close-forgot-password-dialog"
            variant="outlined"
            sx={{
              whiteSpace: 'nowrap',
              color: colors.csaaBlack,
              border: `2px solid ${colors.csaaBlack}`,
              height: { mobile: 36, tablet: 42 },
              mt: { mobile: 3, tablet: 5 },
            }}
            onClick={handleClose}
          >
            {showForm ? 'Cancel' : 'Close'}
          </Button>
          {showForm && (
            <Button
              fullWidth
              data-testid="send-email-button"
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
                height: { mobile: 36, tablet: 42 },
                mt: 2,
              }}
              type="submit"
              disabled={!isValid}
              loading={forgotPasswordMutation.isPending}
            >
              Send email
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
